import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, PhoneCall, Clock, DollarSign, Sparkles, Download, AlertCircle } from "lucide-react";

function fmtDuration(s) {
  if (!s) return "—";
  const m = Math.floor(s / 60);
  const sec = Math.round(s % 60);
  return `${m}m ${sec}s`;
}

export default function CallDetailsDialog({ callId, open, onOpenChange }) {
  const [call, setCall] = useState(null);
  const [loading, setLoading] = useState(false);
  // Recording is proxied through our backend so the VAPI private key never
  // reaches the client, AND so HIPAA-mode calls (whose raw R2 URL rejects
  // anonymous requests) still play. We fetch as blob via authenticated
  // axios and hand an ObjectURL to <audio>/<a href>.
  const [recordingBlobUrl, setRecordingBlobUrl] = useState(null);
  const [recordingError, setRecordingError] = useState("");
  const [recordingLoading, setRecordingLoading] = useState(false);

  const load = async () => {
    if (!callId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/calls/${callId}`);
      setCall(data);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (open && callId) { setCall(null); load(); }
     
  }, [open, callId]);

  // Fetch the recording (once) when the call ends and a recording exists.
  useEffect(() => {
    if (!open || !call?.recording_url || !call?.id) return;
    if (recordingBlobUrl || recordingLoading) return;
    let objUrl = null;
    setRecordingLoading(true); setRecordingError("");
    (async () => {
      try {
        const res = await api.get(`/calls/${call.id}/recording`, { responseType: "blob" });
        objUrl = URL.createObjectURL(res.data);
        setRecordingBlobUrl(objUrl);
      } catch (err) {
        setRecordingError(err?.response?.data?.detail || "Recording unavailable");
      } finally { setRecordingLoading(false); }
    })();
    return () => { if (objUrl) URL.revokeObjectURL(objUrl); };
     
  }, [open, call?.id, call?.recording_url]);

  // Reset recording state when dialog closes so the next open starts clean.
  useEffect(() => {
    if (!open) {
      if (recordingBlobUrl) URL.revokeObjectURL(recordingBlobUrl);
      setRecordingBlobUrl(null); setRecordingError(""); setRecordingLoading(false);
    }
     
  }, [open]);

  // Auto-refresh while the call is still active
  useEffect(() => {
    if (!open || !call) return;
    if (["ended", "completed", "failed", "no-answer", "busy"].includes(call.status)) return;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
     
  }, [open, call?.status]);

  // Build per-turn transcript: prefer messages[], fallback to transcript string
  const turns = call?.messages?.filter((m) => m.role === "user" || m.role === "bot" || m.role === "assistant") || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-auto" data-testid="call-details-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2"><PhoneCall className="w-4 h-4" /> Call details</span>
            {call?.status && (
              <span className={`text-[10px] font-mono px-2 py-0.5 rounded-sm ${call.status === "ended" ? "bg-[#10B981]/10 text-[#10B981]" : call.status === "failed" ? "bg-[#EF4444]/10 text-[#EF4444]" : "bg-[#F3F4F6]"}`}>{call.status}</span>
            )}
          </DialogTitle>
        </DialogHeader>

        {loading && !call && (
          <div className="flex items-center gap-2 text-sm text-[#4B5563] py-6"><Loader2 className="w-4 h-4 animate-spin" /> Loading call details…</div>
        )}

        {call && (
          <div className="space-y-4">
            {/* Top metadata */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
              <div><div className="text-[10px] font-mono uppercase text-[#4B5563]">to</div><div className="font-mono font-bold mt-0.5">{call.to_number}</div></div>
              <div><div className="text-[10px] font-mono uppercase text-[#4B5563]">customer</div><div className="font-medium mt-0.5">{call.customer_name || "—"}</div></div>
              <div><div className="text-[10px] font-mono uppercase text-[#4B5563] flex items-center gap-1"><Clock className="w-3 h-3" /> duration</div><div className="font-mono font-bold mt-0.5">{fmtDuration(call.duration_seconds)}</div></div>
              <div><div className="text-[10px] font-mono uppercase text-[#4B5563] flex items-center gap-1"><DollarSign className="w-3 h-3" /> cost</div><div className="font-mono font-bold mt-0.5">${typeof call.cost === "number" ? call.cost.toFixed(3) : "—"}</div></div>
            </div>

            {call.ended_reason && (
              <div className="text-[11px] text-[#4B5563] flex items-center gap-1.5 font-mono"><AlertCircle className="w-3 h-3" /> ended: <span className="text-[#030712] font-bold">{call.ended_reason}</span></div>
            )}

            {call.vapi_error && (
              <div className="text-xs text-[#EF4444] border border-[#EF4444]/30 bg-[#EF4444]/5 p-3 rounded-sm" data-testid="call-error">{Array.isArray(call.vapi_error) ? call.vapi_error.join(", ") : call.vapi_error}</div>
            )}

            {/* Recording */}
            {call.recording_url && (
              <div>
                <div className="text-[10px] font-mono uppercase text-[#4B5563] mb-1.5">Recording</div>
                {recordingLoading && (
                  <div className="flex items-center gap-2 text-xs text-[#4B5563]" data-testid="call-recording-loading">
                    <Loader2 className="w-3 h-3 animate-spin" /> Fetching secure recording…
                  </div>
                )}
                {recordingError && (
                  <div className="text-xs text-[#EF4444] border border-[#EF4444]/30 bg-[#EF4444]/5 p-2 rounded-sm" data-testid="call-recording-error">
                    {recordingError}
                  </div>
                )}
                {recordingBlobUrl && (
                  <div className="flex items-center gap-2">
                    <audio src={recordingBlobUrl} controls className="w-full" data-testid="call-recording-audio" />
                    <a
                      href={recordingBlobUrl}
                      download={`call-${call.id}.wav`}
                      className="btn-outline text-xs flex items-center gap-1"
                      data-testid="call-recording-download"
                    >
                      <Download className="w-3 h-3" />
                    </a>
                  </div>
                )}
              </div>
            )}

            {/* AI Summary */}
            {call.summary && (
              <div className="hs-card p-3 bg-[#0044FF]/5 border-[#0044FF]/20" data-testid="call-summary">
                <div className="text-[10px] font-mono uppercase text-[#0044FF] flex items-center gap-1"><Sparkles className="w-3 h-3" /> AI summary</div>
                <div className="text-sm mt-1.5 leading-relaxed">{call.summary}</div>
                {call.analysis?.structuredData && Object.keys(call.analysis.structuredData).length > 0 && (
                  <div className="mt-2 grid grid-cols-2 gap-2 text-[11px]">
                    {Object.entries(call.analysis.structuredData).map(([k, v]) => (
                      <div key={k} className="font-mono"><span className="text-[#4B5563]">{k}:</span> <span className="font-bold">{String(v)}</span></div>
                    ))}
                  </div>
                )}
                {call.analysis?.successEvaluation && (
                  <div className="mt-2 text-[11px] font-mono">success: <span className="font-bold">{String(call.analysis.successEvaluation)}</span></div>
                )}
              </div>
            )}

            {/* Contact data (for campaign calls) */}
            {call.contact_data && Object.keys(call.contact_data).filter((k) => k !== "phone").length > 0 && (
              <div className="hs-card p-3">
                <div className="text-[10px] font-mono uppercase text-[#4B5563]">Customer record (from CSV)</div>
                <div className="grid grid-cols-2 gap-1.5 mt-1.5 text-xs">
                  {Object.entries(call.contact_data).filter(([k]) => k !== "phone").map(([k, v]) => (
                    <div key={k}><span className="font-mono text-[#4B5563]">{k}:</span> <span>{v}</span></div>
                  ))}
                </div>
              </div>
            )}

            {/* Transcript */}
            {turns.length > 0 ? (
              <div>
                <div className="text-[10px] font-mono uppercase text-[#4B5563] mb-2">Transcript</div>
                <div className="space-y-2 max-h-[360px] overflow-auto" data-testid="call-transcript">
                  {turns.map((m, i) => {
                    const isUser = m.role === "user";
                    return (
                      <div key={i} className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
                        <div className={`max-w-[80%] px-3 py-2 text-sm rounded-sm ${isUser ? "bg-[#0044FF] text-white" : "bg-[#F3F4F6]"}`}>
                          <div className="text-[10px] font-mono opacity-60 mb-1">{isUser ? "USER" : "ASSISTANT"}{m.time ? ` · ${new Date(m.time).toLocaleTimeString()}` : ""}</div>
                          <div>{m.message || m.content}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : call.transcript ? (
              <div>
                <div className="text-[10px] font-mono uppercase text-[#4B5563] mb-2">Transcript</div>
                <pre className="font-mono text-[11px] bg-[#F3F4F6] p-3 rounded-sm whitespace-pre-wrap leading-relaxed max-h-[360px] overflow-auto" data-testid="call-transcript-text">{call.transcript}</pre>
              </div>
            ) : (
              <div className="text-xs text-[#4B5563] italic">
                {(call.status === "queued" || call.status === "ringing" || call.status === "in-progress") ? "Transcript will appear here once the call ends. Refreshing automatically…" : "No transcript available."}
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
